import { type ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CloseIcon, MenuIcon } from "./icons";
import { Lockup } from "./Lockup";

/**
 * The panel's chrome: the lockup, whatever names the current scope, the account chip, a nav,
 * and the screen itself.
 *
 * **It fills the viewport rather than sitting in a centred column**, which is the #293
 * wireframes' shape and the first thing that was wrong here. The header is a full-bleed band
 * on the surface tone with a hairline under it; the sidebar is a real panel with its own
 * surface and a rule down its edge; the content pane takes what is left and scrolls on its
 * own. A centred `max-width` *shell* reads as a document, and this is an app.
 *
 * **The screen inside the pane is a column.** Line length is still `68ch` on the prose
 * (`.screen__blurb`). The page those sentences sit on is `--content`, so a heading and the
 * Add that belongs to it stay on the same object as the cards under them — the failure a
 * laptop window showed when only the heading row was capped. The screen *head* stays
 * full-bleed so its hairline remains a pane divider; only the column inside it is capped.
 *
 * **The header is constant and the sidebar is what switches scope.** You are looking at a
 * family or at yourself, never both, and the account chip is the doorway between them — so
 * `nav` changes and everything else here does not.
 *
 * **The account chip may not disappear at any width**, which is why it is a child of the
 * header rather than of the nav. #293 weighed a per-visit disclosure gate against an
 * always-present chip and chose the chip, so the chip *is* the whole of #104 here; a layout
 * that dropped it on a phone would drop the disclosure with it.
 *
 * **Narrow is a drawer, not a rearrangement.** The wireframes are explicit that it holds
 * exactly what the sidebar holds, in the same order — one structure to learn rather than a
 * phone version of the app — over a scrim, with the header untouched behind it. So the same
 * `nav` renders in both, and only its container moves.
 */
export function Frame({
  scope,
  account,
  nav,
  children,
}: {
  /** What this scope is called — the family switcher, or "You". Absent when there is none. */
  scope?: ReactNode;
  account: ReactNode;
  /** The sections. Absent for a state with nothing to point at, which drops the sidebar. */
  nav?: ReactNode;
  children: ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Any navigation closes it. A drawer that survived the tap that used it would cover the
  // screen it just opened, which on a phone is the whole page. `location.pathname` is not read
  // in the effect — it IS the trigger, which is what the rule below cannot see.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the path change is the trigger.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  return (
    <div className="shell">
      <header className="shell__header">
        {nav === undefined ? null : (
          <button
            className="shell__drawer-toggle"
            type="button"
            aria-label={drawerOpen ? "Close sections" : "Open sections"}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((was) => !was)}
          >
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        )}
        <Lockup size="chrome" />
        {scope === undefined ? null : <div className="shell__scope">{scope}</div>}
        <div className="shell__spacer" />
        <div className="shell__account">{account}</div>
      </header>

      <div className="shell__body">
        {nav === undefined ? null : (
          <>
            <nav
              className={`shell__nav${drawerOpen ? " shell__nav--open" : ""}`}
              aria-label="Sections"
            >
              {nav}
            </nav>
            {/* The scrim is a button so dismissing the drawer is reachable without a pointer,
                and it exists only while the drawer is open — a permanent invisible overlay is
                how a page stops responding to clicks for reasons nobody can see. */}
            {drawerOpen ? (
              <button
                className="shell__scrim"
                type="button"
                aria-label="Close sections"
                onClick={() => setDrawerOpen(false)}
              />
            ) : null}
          </>
        )}
        <main className="shell__main">{children}</main>
      </div>
    </div>
  );
}
